<template>
  <b-row class="justify-content-center">
    <b-col
      cols="12"
      md="7"
    >
      <paper-simple>
        <div class="px-2">
          <input-field
            label="Storage Accounts"
            label-position="top"
          >
            <v-select
              v-model="selectedAccount"
              placeholder="Choose Account"
              class="w-100 type-select distro-single-select"
              label="username"
              :searchable="false"
              :allow-empty="false"
              :options="externalAccounts"
              @input="changeStorageAccount(selectedAccount)"
            />

          </input-field>
        </div>
      </paper-simple>
      <paper-simple v-show="showDatacenterRegions">
        <div class="px-2">
          <custom-input-radio
            v-model="bucketSettings.region"
            label="Choose a datacenter region"
            :options="bucketRegions"
          />
          <custom-alert
            items-center
            class="mt-1"
            icon-name="Info"
          >
            {{ bucketsInfo.datacenterRegion.message }}
          </custom-alert>
        </div>
      </paper-simple>
      <paper-simple>
        <div class="px-2">
          <custom-input-radio
            v-model="bucketSettings.fileListing"
            label="Choose file listing type"
            :options="fileListingOptions"
          />
          <custom-alert icon-name="Info">
            <strong>Important:</strong>
            This setting has no effect on whether individual files are visible.
            It only determines if anonymous users can list the name, size, and
            other metadata for the files in this Space.
          </custom-alert>
        </div>
      </paper-simple>
      <paper-simple>
        <div class="px-2">
          <input-field
            name="bucket-subdomain"
            label="Choose a unique subdomain for your bucket*"
            label-position="top"
            info-position="label"
            :info="bucketsInfo.subdomain"
            :error="
              !subdomainIsValid && validateInitialized && !validatingSubdomain
                ? 'Subdomain is not valid '
                : null
            "
            :success="
              subdomainIsValid && validateInitialized && !validatingSubdomain
                ? 'Subdomain is valid'
                : null
            "
            @openInfo="openInfo"
          >
            <b-input-group>
              <template #append>
                <b-button
                  variant="bg-white"
                  class="border-left-0"
                  :style="`color: ${colorName}`"
                >
                  <span :key="`${bucketSettings.subdomain.length}-bucketName`">
                    <div
                      v-if="validatingSubdomain"
                      class="loading"
                    >
                      <GjIcon name="Loader" />
                    </div>
                    <GjIcon
                      v-else
                      :key="`icon-${iconName}`"
                      :name="iconName"
                    />
                  </span>
                </b-button>
              </template>
              <b-form-input
                id="bucket-subdomain"
                v-model="bucketSettings.subdomain"
                placeholder="Enter subdomain here..."
                @input="validateSubdomain"
              />
            </b-input-group>
          </input-field>
          <input-field
            name="bucket-origin_url"
            label="Your bucket’s origin URL"
            label-position="top"
          >
            <b-input-group>
              <template #append>
                <b-button
                  v-clipboard:copy="
                    `${
                      selectedAccount.endpoint
                    }/${bucketSettings.subdomain || '[subdomain]'}`
                  "
                  v-clipboard:success="onCopy"
                  variant="bg-gray"
                  class="border-left-0"
                >
                  <span>
                    <GjIcon name="Copy" />
                  </span>
                </b-button>
              </template>
              <b-form-input
                id="bucket-origin_url"
                :value="
                  `${
                    selectedAccount.endpoint
                  }/${bucketSettings.subdomain || '[subdomain]'}`
                "
                readonly
              />
            </b-input-group>
          </input-field>
        </div>
      </paper-simple>
      <paper-simple>
        <div class="px-2">
          <custom-input-radio
            v-model="bucketSettings.version"
            label="Bucket Versioning"
            :options="bucketVersioningOptions"
          />
          <custom-alert icon-name="Info">
            Versioning is a means of keeping multiple variants of an object in
            the same bucket. You can use versioning to preserve, retrieve, and
            restore every version of every object stored in your bucket. With
            versioning, you can easily recover from both unintended user actions
            and application failures
          </custom-alert>
        </div>
      </paper-simple>
      <paper-simple v-if="areTagsSupported">
        <div class="mx-n2">
          <validation-observer ref="rules">
            <tags
              @updateTags="updateTags"
              @openInfo="openInfo"
            />
          </validation-observer>
        </div>
      </paper-simple>
    </b-col>
    <b-col cols="12">
      <div class="d-flex justify-content-end py-2 border-default border-t mt-1">
        <b-button
          variant="bg-white"
          :to="{ name: 'buckets' }"
        >
          Cancel
        </b-button>
        <button-loading
          variant="bg-primary"
          class="ml-1"
          :spinning="addingBucket"
          :disabled="
            !subdomainIsValid ||
              (validatingSubdomain && !!!bucketSettings.subdomain) ||
              addingBucket
          "
          @click="validation()"
        >
          Create Bucket
        </button-loading>
      </div>
    </b-col>
    <info
      v-if="infoData"
      :info="infoData"
      @close="infoData = null"
    />
  </b-row>
</template>

<script>
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import { ValidationObserver } from 'vee-validate'
import Tags from './Tags.vue'
import bucketsInfo from '../bucketsInfo'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'BucketForm',
  components: {
    CustomInputRadio,
    Tags,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      bucketsInfo,
      validateInitialized: false,
      validatingSubdomain: false,
      validatingTimeoutStarted: false,
      bucketSettings: null,
      infoData: null,
      subdomain: '',
      subdomainIsValid: false,
      newTags: [],
      datacenterRegionOptions: [
        { text: 'EU West', value: 1 },
        { text: 'EU East', value: 2 },
      ],
      fileListingOptions: [
        {
          text: 'Restrict File Listing',
          value: false,
          description: 'Only users with the access key can list the contents',
        },
        {
          text: 'Enable File Listing',
          value: true,
          description: 'Anyone can list the content of this bucket',
        },
      ],
      bucketVersioningOptions: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      objectLock: true,
      objectLockOptions: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      showDatacenterRegions: true,
    }
  },
  computed: {
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
        .filter(account => !account.bucketName) // Filter out accounts with a bucketName
        .map(account => ({
          ...account,
          providerName: account.providerName === null ? 'Captain' : account.providerName,
          username: account.parentId === '0' ? 'Captain' : account.username,
        }))
    },
    externalAccountsNoFilters() {
      return this.$store.state.externalAccount.externalAccounts
    },
    selectedAccount: {
      get() {
        const account = this.externalAccounts.find(
          a => a.id === this.$store.state.externalAccount.externalAccount?.id,
        )
        return account || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    emptyBucket() {
      return this.$store.state.accountBuckets.bucket
    },
    objectLockSettings() {
      return this.bucketSettings?.objectLock
    },
    versioningSettings() {
      return this.bucketSettings?.version
    },
    computedAccountId() {
      return this.$route.params.accountId
    },
    addingBucket() {
      return this.$store.state.accountBuckets.addingBucket
    },
    bucketRegions() {
      return this.bucketSettings
        ? this.bucketSettings.regionOptions.map(region => ({
          ...region,
          text: region.name,
        }))
        : []
    },
    colorName() {
      if (
        this.validatingSubdomain
        || (!this.validateInitialized && this.bucketSettings.subdomain === '')
      ) {
        return '#667c99'
      }
      return this.subdomainIsValid ? '#36b37e' : '#e34850'
    },
    iconName() {
      if (!this.validateInitialized && this.bucketSettings.subdomain === '') {
        return 'Question'
      }
      return this.subdomainIsValid ? 'Checkmark' : 'InfoAlt'
    },
    areTagsSupported() {
      return (
        this.selectedAccount?.accountType === 0
        || this.selectedAccount?.providerName === 'Amazon'
      )
    },
  },
  watch: {
    objectLockSettings(val) {
      if (val) {
        this.bucketSettings.version = true
      }
    },
    versioningSettings(val) {
      if (!val) {
        this.bucketSettings.objectLock = false
      }
    },
  },
  created() {
    this.bucketSettings = { ...this.emptyBucket, subdomain: '' }
    this.checkAndRedirect(this.computedAccountId)
  },
  methods: {
    validateLocal(str) {
      if (
        this.bucketSettings.subdomain
        && this.bucketSettings.subdomain.length > 2
        && this.bucketSettings.subdomain.length < 64
      ) {
        const replaceKey = '__REPLACE__'
        const reg = `(^[0-9a-z]${replaceKey}[0-9a-z]*[0-9a-z]$|^[0-9a-z][0-9a-z]*[0-9a-z]$)`
        const n = str.match(/\.|-/g) || []
        let nStr = ''
        n.forEach(() => {
          nStr += '[0-9a-z]+[-.]{1}'
        })
        const regString = reg.replace(replaceKey, nStr)
        const re = new RegExp(regString)
        return re.test(str)
      }
      return false
    },
    checkAndRedirect(id) {
      // eslint-disable-next-line eqeqeq
      const matchingElement = this.externalAccountsNoFilters?.find(item => item.id == id)
      if (matchingElement.bucketName) {
        this.$router.push({ name: 'object-files', params: { id: matchingElement.bucketName, accountId: id } })
      }
    },
    updateTags(val) {
      this.newTags = val
    },
    async validateSubdomain() {
      this.validateInitialized = true
      setTimeout(() => {
        if (this.validateLocal(this.bucketSettings.subdomain)) {
          this.validatingSubdomain = true
          this.$store
            .dispatch('accountBuckets/validateSubdomain', {
              accountId: this.selectedAccount.id,
              name: { subdomain: this.bucketSettings.subdomain },
            })
            .then(isValid => {
              this.subdomainIsValid = isValid
              this.validatingSubdomain = false
            })
            .catch(() => {})
        } else {
          this.subdomainIsValid = false
        }
      }, 500)
    },
    openInfo(data) {
      this.infoData = data
    },
    onCopy() {
      this.infoToast('Copied to clipboard', 'Clipboard')
    },
    createBucket() {
      const tags = this.formatTags()
      const query = {
        accountId: this.selectedAccount?.id,
        bucket: {
          bucketName: this.bucketSettings.subdomain,
          bucketRegion: this.bucketSettings.region,
          // objectLock: this.objectLock,
          version: this.bucketSettings.version,
          fileListing: this.bucketSettings.fileListing,
          tags,
        },
      }

      if (query.bucket.bucketRegion === 1) {
        this.infoToast('EU West data center required temporarily.')
      } else {
        this.$store
          .dispatch('accountBuckets/addBucket', query)
          .then(() => {
            this.successToast('Bucket was created')
            this.$router.push({ name: 'buckets' })
          })
          .catch(error => {
            const errorMessage = error.response?.data?.errors[0] || 'Something went wrong while creating bucket.'
            this.errorToast(errorMessage)
          })
      }
    },
    formatTags() {
      const tags = []

      if (!this.areTagsSupported) {
        return []
      }

      const innerTags = this.newTags.filter(
        (v, i, a) => a.findIndex(
          t => t.tagKey === v.tagKey && t.tagKey !== '' && t.tagValue !== '',
        ) === i,
      )

      for (let i = 0; i < innerTags.length; i++) {
        tags.push({
          key: innerTags[i].tagKey,
          value: innerTags[i].tagValue,
        })
      }

      return tags
    },
    validation() {
      return new Promise((resolve, reject) => {
        if (!this.areTagsSupported) {
          resolve(true)
          this.createBucket()
        }
        this.$refs.rules.validate().then(success => {
          if (success) {
            resolve(true)
            this.createBucket()
          } else {
            reject()
          }
        })
      })
    },
    changeStorageAccount(account) {
      if (account) {
        this.showDatacenterRegions = account.parentId === '0'
        this.bucketSettings.endpoint = account.endpoint
      }
    },

  },
}
</script>
